/**
 * Return-to-Play Protocol Service
 * 
 * Handles all RTP-related API calls including:
 * - Starting/managing RTP protocols
 * - Phase progression/regression
 * - Dashboard data
 * - Exercise library
 */

import { HTTP } from "./http";

let API_SERVER = "";
if (window.location.href.indexOf("8080") > -1) {
  API_SERVER = "http://www.localhost:8888/back-end-endpoint-rtp.php";
} else {
  API_SERVER = "/personal-training-BE/back-end-endpoint-rtp.php";
}

// Phase definitions for frontend reference
export const RTP_PHASES = {
  1: {
    name: 'Acute/Rest',
    shortName: 'P1',
    color: '#dc3545',
    bgColor: '#f8d7da',
    description: 'Rest and initial recovery. Focus on reducing inflammation and pain.',
    leadRoles: ['Physio', 'Head Physio'],
    minDuration: '24-48 hours',
    leadDisplay: 'Physio / Medical',
    activities: 'Pain-free daily activities and early mobilisation',
    progressionCriteria: [
      'Pain-free at rest',
      'Minimal swelling/inflammation',
      'Full passive range of motion',
      'Able to walk pain-free'
    ]
  },
  2: {
    name: 'Light Aerobic',
    shortName: 'P2',
    color: '#fd7e14',
    bgColor: '#ffe5d0',
    description: 'Light cardiovascular exercise. Heart rate under 70% max.',
    leadRoles: ['Physio', 'Head Physio', 'Sports Science'],
    minDuration: '24-48 hours',
    leadDisplay: 'Physio / Sports Science',
    activities: 'Walking, stationary bike, pool work. HR <70% max',
    progressionCriteria: [
      'No pain during light aerobic activity',
      'No swelling after activity',
      'Can complete 15-20 mins continuous activity',
      'Normal gait pattern'
    ]
  },
  3: {
    name: 'Sport-Specific',
    shortName: 'P3',
    color: '#ffc107',
    bgColor: '#fff3cd',
    description: 'Football-specific drills without contact. Ball work and agility.',
    leadRoles: ['Sports Science', 'Head Sports Science'],
    minDuration: '24-48 hours',
    leadDisplay: 'Sports Science',
    activities: 'Running drills, ball work, agility. No contact',
    progressionCriteria: [
      'Pain-free during sport-specific movements',
      'Full range of motion under load',
      'Completed progressive running program',
      'Passed agility/change of direction tests'
    ]
  },
  4: {
    name: 'Non-Contact Training',
    shortName: 'P4',
    color: '#20c997',
    bgColor: '#d1f2eb',
    description: 'Full training participation without physical challenges.',
    leadRoles: ['Sports Science', 'Head Sports Science', 'Coach', 'HeadCoach'],
    minDuration: '24-48 hours',
    leadDisplay: 'Sports Science / Coach',
    activities: 'Full training drills, tactical work. No tackling/heading',
    progressionCriteria: [
      'Completed full training session without symptoms',
      'Confidence in movements',
      'Coach assessment of readiness',
      'No adverse reactions 24hrs post-training'
    ]
  },
  5: {
    name: 'Full Contact/Clearance',
    shortName: 'P5',
    color: '#28a745',
    bgColor: '#d4edda',
    description: 'Full contact training and match readiness assessment.',
    leadRoles: ['Head Sports Science'],
    minDuration: '24-48 hours',
    leadDisplay: 'Head Sports Science',
    activities: 'Full contact training, match simulation',
    progressionCriteria: [
      'Completed full contact training',
      'Passed fitness testing (if required)',
      'Medical clearance obtained',
      'Player confident and ready'
    ]
  }
};

// Role permissions for phase progression
export const PHASE_PROGRESSION_PERMISSIONS = {
  '1_2': ['Physio', 'Head Physio', 'Admin'],
  '2_3': ['Physio', 'Head Physio', 'Sports Science', 'Head Sports Science', 'Admin'],
  '3_4': ['Sports Science', 'Head Sports Science', 'Admin'],
  '4_5': ['Head Sports Science', 'Admin'],
};

/**
 * Check if a user role can progress from one phase to another
 * @param {number} fromPhase - Current phase
 * @param {number} toPhase - Target phase
 * @param {string} userRole - User's position/role
 * @returns {boolean}
 */
export function canProgressPhase(fromPhase, toPhase, userRole) {
  const key = `${fromPhase}_${toPhase}`;
  const allowedRoles = PHASE_PROGRESSION_PERMISSIONS[key];
  return allowedRoles && allowedRoles.includes(userRole);
}

/**
 * Get required roles for a phase progression
 * @param {number} fromPhase - Current phase
 * @param {number} toPhase - Target phase
 * @returns {string[]}
 */
export function getRequiredRoles(fromPhase, toPhase) {
  const key = `${fromPhase}_${toPhase}`;
  return PHASE_PROGRESSION_PERMISSIONS[key] || [];
}

/**
 * Get phase info by number
 * @param {number} phaseNumber 
 * @returns {object}
 */
export function getPhaseInfo(phaseNumber) {
  return RTP_PHASES[phaseNumber] || null;
}

/**
 * Get minimum hours between phases based on maturity
 * @param {string} maturityCategory - 'Pre-PHV', 'Circa-PHV', 'Post-PHV'
 * @returns {number}
 */
export function getMinHoursBetweenPhases(maturityCategory) {
  return maturityCategory === 'Circa-PHV' ? 48 : 24;
}

const ReturnToPlayService = {
  /**
   * Start RTP Protocol for an injury
   * @param {object} data - { injury_id, phase_lead_id, phase_lead_name, phase_lead_role, restrictions, target_date, next_review_date }
   */
  startRTPProtocol(data) {
    return HTTP.post(API_SERVER, {
      action: 'startRTPProtocol',
      ...data
    });
  },

  /**
   * Progress to next RTP phase
   * @param {object} data - { injury_id, user_id, user_name, user_role, sign_off_comments, restrictions, phase_lead_id, phase_lead_name, phase_lead_role, next_review_date }
   */
  progressRTPPhase(data) {
    return HTTP.post(API_SERVER, {
      action: 'progressRTPPhase',
      ...data
    });
  },

  /**
   * Regress to earlier phase (setback)
   * @param {object} data - { injury_id, user_id, user_name, user_role, adverse_reaction, new_phase, restrictions, sign_off_comments }
   */
  regressRTPPhase(data) {
    return HTTP.post(API_SERVER, {
      action: 'regressRTPPhase',
      ...data
    });
  },

  /**
   * Get RTP data for a specific injury
   * @param {number} injuryId 
   */
  getRTPByInjuryId(injuryId) {
    return HTTP.post(API_SERVER, {
      action: 'getRTPByInjuryId',
      injury_id: injuryId
    });
  },

  /**
   * Get RTP Dashboard (squad view)
   * @param {object} data - { clubId, teamId (optional) }
   */
  getRTPDashboard(data) {
    return HTTP.post(API_SERVER, {
      action: 'getRTPDashboard',
      ...data
    });
  },

  /**
   * Get Exercise Library
   * @param {object} data - { clubId, phase_number (optional), maturity_category (optional), include_inactive (optional) }
   */
  getExerciseLibrary(data = {}) {
    return HTTP.post(API_SERVER, {
      action: 'getExerciseLibrary',
      ...data
    });
  },

  /**
   * Add new exercise to the library
   * @param {object} data - { clubId, exercise_name, phase_number, category, description, sets_reps, duration, intensity, difficulty, precautions, video_url, video_file_path, maturity_category, injury_type, created_by }
   */
  addExercise(data) {
    return HTTP.post(API_SERVER, {
      action: 'addExercise',
      ...data
    });
  },

  /**
   * Update an existing exercise
   * @param {object} data - { exercise_id, clubId, ... exercise fields }
   */
  updateExercise(data) {
    return HTTP.post(API_SERVER, {
      action: 'updateExercise',
      ...data
    });
  },

  /**
   * Delete (soft delete) an exercise
   * @param {number} exerciseId 
   * @param {string} clubId 
   */
  deleteExercise(exerciseId, clubId) {
    return HTTP.post(API_SERVER, {
      action: 'deleteExercise',
      exercise_id: exerciseId,
      clubId: clubId
    });
  },

  /**
   * Duplicate an exercise (clone for modification)
   * @param {number} exerciseId 
   * @param {string} clubId 
   * @param {object} options - { new_name, phase_number, difficulty, maturity_category, created_by }
   */
  duplicateExercise(exerciseId, clubId, options = {}) {
    return HTTP.post(API_SERVER, {
      action: 'duplicateExercise',
      exercise_id: exerciseId,
      clubId: clubId,
      ...options
    });
  },

  /**
   * Toggle exercise active/inactive status
   * @param {number} exerciseId 
   * @param {string} clubId 
   */
  toggleExerciseActive(exerciseId, clubId) {
    return HTTP.post(API_SERVER, {
      action: 'toggleExerciseActive',
      exercise_id: exerciseId,
      clubId: clubId
    });
  },

  /**
   * Upload exercise file (image, PDF, video, attachment)
   * @param {string} clubId 
   * @param {string} fileName 
   * @param {string} fileData - Base64 encoded file data
   * @param {string} fileType - 'image', 'pdf', 'video', 'attachment'
   */
  uploadExerciseFile(clubId, fileName, fileData, fileType = 'attachment') {
    return HTTP.post(API_SERVER, {
      action: 'uploadExerciseFile',
      clubId: clubId,
      file_name: fileName,
      file_data: fileData,
      file_type: fileType
    });
  },

  /**
   * Update RTP details (restrictions, review date, target date)
   * @param {object} data - { injury_id, restrictions, next_review_date, target_date }
   */
  updateRTPDetails(data) {
    return HTTP.post(API_SERVER, {
      action: 'updateRTPDetails',
      ...data
    });
  },

  /**
   * Complete RTP Protocol (final clearance)
   * @param {object} data - { injury_id, user_id, user_name, user_role, sign_off_comments }
   */
  completeRTP(data) {
    return HTTP.post(API_SERVER, {
      action: 'completeRTP',
      ...data
    });
  },

  /**
   * Get all injuries for a player with RTP status
   * @param {number} playerId
   */
  getPlayerInjuriesWithRTP(playerId) {
    return HTTP.post(API_SERVER, {
      action: 'getPlayerInjuriesWithRTP',
      player_id: playerId
    });
  },

  // ========================================================================
  // EXERCISE ASSIGNMENT & COMPLETION TRACKING
  // ========================================================================

  /**
   * Assign a single exercise to a player's RTP
   * @param {object} data - { injury_id, player_id, exercise_id, phase_number, assigned_by, custom_sets_reps, custom_notes, frequency }
   */
  assignExerciseToPlayer(data) {
    return HTTP.post(API_SERVER, {
      action: 'assignExerciseToPlayer',
      ...data
    });
  },

  /**
   * Assign multiple exercises to a player's RTP
   * @param {object} data - { injury_id, player_id, exercise_ids[], phase_number, assigned_by }
   */
  assignExercisesToPlayer(data) {
    return HTTP.post(API_SERVER, {
      action: 'assignExercisesToPlayer',
      ...data
    });
  },

  /**
   * Remove an exercise assignment (soft delete)
   * @param {number} assignmentId - The rtp_player_exercises.id
   * @param {number} userId - Who is removing
   */
  removeExerciseAssignment(assignmentId, userId) {
    return HTTP.post(API_SERVER, {
      action: 'removeExerciseAssignment',
      assignment_id: assignmentId,
      user_id: userId
    });
  },

  /**
   * Get exercises assigned to a player's injury/RTP
   * @param {object} data - { injury_id, player_id, include_completed (optional), date (optional) }
   */
  getPlayerAssignedExercises(data) {
    return HTTP.post(API_SERVER, {
      action: 'getPlayerAssignedExercises',
      ...data
    });
  },

  /**
   * Mark an exercise as completed for today
   * @param {object} data - { assignment_id, player_id, injury_id, completed_by, pain_level, difficulty_rating, notes }
   */
  completeExercise(data) {
    return HTTP.post(API_SERVER, {
      action: 'completeExercise',
      ...data
    });
  },

  /**
   * Remove today's completion (undo)
   * @param {number} completionId
   */
  undoExerciseCompletion(completionId) {
    return HTTP.post(API_SERVER, {
      action: 'undoExerciseCompletion',
      completion_id: completionId
    });
  },

  /**
   * Get exercise completion history
   * @param {object} data - { player_id, injury_id (optional), assignment_id (optional), start_date (optional), end_date (optional) }
   */
  getExerciseCompletionHistory(data) {
    return HTTP.post(API_SERVER, {
      action: 'getExerciseCompletionHistory',
      ...data
    });
  },

  /**
   * Get player's RTP compliance stats
   * @param {object} data - { player_id, injury_id, days (optional, default 7) }
   */
  getPlayerComplianceStats(data) {
    return HTTP.post(API_SERVER, {
      action: 'getPlayerComplianceStats',
      ...data
    });
  },

  /**
   * Get player's active RTP with today's exercises
   * Used on player dashboard for "My RTP" section
   * @param {number} playerId
   */
  getPlayerActiveRTP(playerId) {
    return HTTP.post(API_SERVER, {
      action: 'getPlayerActiveRTP',
      player_id: playerId
    });
  },

  /**
   * Update playing/training status from RTP view
   * @param {object} data - { injuryId, playingStatus, trainingStatus }
   */
  updateRTPStatus(data) {
    return HTTP.post(API_SERVER, {
      action: 'updateRTPStatus',
      ...data
    });
  },

  /**
   * Get full injury details for RTP view
   * @param {number} injuryId
   */
  getInjuryDetailsForRTP(injuryId) {
    return HTTP.post(API_SERVER, {
      action: 'getInjuryDetailsForRTP',
      injuryId: injuryId
    });
  },

  /**
   * Set RTP phase directly (for automated transitions, skips role/time checks)
   * @param {object} data - { injuryId, playerId, clubId, targetPhase }
   */
  setRTPPhase(data) {
    return HTTP.post(API_SERVER, {
      action: 'setRTPPhase',
      ...data
    });
  },

  // ========================================================================
  // RECOVERY DRILL ASSIGNMENTS (Training Library → RTP)
  // ========================================================================

  /**
   * Get all Recovery-section drills for a club (for the picker)
   * @param {string} clubId
   */
  getRecoveryDrillsForClub(clubId) {
    return HTTP.post(API_SERVER, { action: 'getRecoveryDrillsForClub', clubId });
  },

  /**
   * Get Recovery drills assigned to a player's injury for a specific phase
   * @param {number|string} injuryId
   * @param {number} phaseNumber
   */
  getPlayerRecoveryDrillAssignments(injuryId, phaseNumber) {
    return HTTP.post(API_SERVER, { action: 'getPlayerRecoveryDrillAssignments', injuryId, phaseNumber });
  },

  /**
   * Assign a Recovery drill to a player's RTP phase
   * @param {object} data - { injury_id, player_id, training_plan_id, phase_number, assigned_by }
   */
  assignRecoveryDrillToPlayer(data) {
    return HTTP.post(API_SERVER, { ...data, action: 'assignRecoveryDrillToPlayer' });
  },

  /**
   * Remove a Recovery drill assignment (soft delete)
   * @param {number} id - rtp_recovery_drill_assignments.id
   */
  removeRecoveryDrillAssignment(id) {
    return HTTP.post(API_SERVER, { action: 'removeRecoveryDrillAssignment', id });
  }
};

export { ReturnToPlayService };
export default ReturnToPlayService;
